import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import headerImg from "../../assets/Images/sda_header_logo-removebg-preview.png";
import { CreateInterestForm } from "../../@types";
import { AppDispatch, RootState } from "../../store";
import { SnackbarMessage } from "../../components/Snackbar";
import { createInterestForm } from "../../actions/interests";

const SCHOOL_OPTIONS: readonly string[] = [
  "San Jose State University",
  "University of Southern California",
  "California State University, Fresno",
  "University of Nevada, Las Vegas",
  "California State University, Sacramento",
  "University of California, Irvine",
  "California State University, Fullerton",
  "University of California, Riverside",
  "University of California, Merced",
  "University of California, Davis",
  "University of California, Santa Barbara",
  "California State Polytechnic University, Pomona",
];

const initialForm: CreateInterestForm = {
  full_name: "",
  phone_number: "",
  email_address: "",
  major: "",
  school: "",
};

const emailOk = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const phoneOk = (v: string) => /^[0-9()\-\s+]{7,20}$/.test(v.trim());

type FieldErrors = Partial<Record<keyof CreateInterestForm, string>>;

const InterestForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const globalStatus = useSelector((s: RootState) => s.interests?.status);

  const [form, setForm] = useState<CreateInterestForm>(initialForm);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [snacks, setSnacks] = useState<SnackbarMessage[]>([]);
  const pushSnack = (msg: SnackbarMessage) => setSnacks((p) => [...p, msg]);

  const [website, setWebsite] = useState("");

  const onChange =
    (name: keyof CreateInterestForm) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

  const onSelectChange =
    (name: keyof CreateInterestForm) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

  const validate = (): boolean => {
    const next: FieldErrors = {};
    if (!form.full_name.trim()) next.full_name = "Full name is required.";
    if (!form.email_address.trim()) next.email_address = "Email is required.";
    else if (!emailOk(form.email_address)) next.email_address = "Enter a valid email.";
    if (!form.phone_number.trim()) next.phone_number = "Phone number is required.";
    else if (!phoneOk(form.phone_number)) next.phone_number = "Enter a valid phone (7–20 chars).";
    if (!form.major.trim()) next.major = "Major is required.";
    if (!form.school.trim()) next.school = "School is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);

    if (website.trim() !== "") {
      setSuccessMsg("Thanks! Your form has been received.");
      setForm(initialForm);
      setWebsite("");
      return;
    }

    if (!validate()) return;

    try {
      setSubmitting(true);
      await dispatch(createInterestForm(form)).unwrap();
      setSuccessMsg("Thanks! Your interest form has been submitted.");
      pushSnack({ message: "Interest form submitted", severity: "success" } as SnackbarMessage);
      setForm(initialForm);
    } catch (err: any) {
      const apiMsg =
        typeof err === "string"
          ? err
          : err?.detail || err?.message || "Something went wrong. Please try again.";
      setErrorMsg(apiMsg);
      pushSnack({ message: apiMsg, severity: "error" } as SnackbarMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const isLoading = submitting || globalStatus === "pending";

  return (
    <div className="min-h-screen bg-black text-gray-100">
      {/* Header */}
      <header className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex flex-col-reverse items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl">Interest Form</h1>
            <p className="mt-1 text-sm text-gray-400">Let us know a bit about you.</p>
          </div>
          <a href="/" className="inline-flex">
            <img src={headerImg} alt="SDA" className="h-10 w-auto opacity-90 sm:h-12" />
          </a>
        </div>
      </header>

      {/* Main */}
      <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <div className="mx-auto w-full max-w-2xl rounded-2xl border border-gray-800 bg-gray-950/60 p-5 sm:p-6 lg:p-8 shadow-lg">
          {successMsg && (
            <div role="status" aria-live="polite" className="mb-4 rounded-md border border-green-400 bg-green-50 px-4 py-3 text-green-800">
              {successMsg}
            </div>
          )}
          {errorMsg && (
            <div role="alert" aria-live="assertive" className="mb-4 rounded-md border border-red-400 bg-red-50 px-4 py-3 text-red-800">
              {errorMsg}
            </div>
          )}

          <form onSubmit={onSubmit} noValidate className="space-y-6">
            {/* Honeypot */}
            <div className="hidden">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="col-span-1">
                <label className="mb-1 block text-sm font-medium text-gray-200">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  autoComplete="name"
                  value={form.full_name}
                  onChange={onChange("full_name")}
                  className="w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-2.5 text-gray-100 outline-none ring-indigo-500/30 focus:border-indigo-500 focus:ring-2"
                  placeholder="Jane Doe"
                  required
                  aria-invalid={!!errors.full_name}
                  aria-describedby={errors.full_name ? "err-full_name" : undefined}
                />
                {errors.full_name && (
                  <p id="err-full_name" className="mt-1 text-sm text-red-400">
                    {errors.full_name}
                  </p>
                )}
              </div>

              <div className="col-span-1">
                <label className="mb-1 block text-sm font-medium text-gray-200">Phone Number</label>
                <input
                  type="tel"
                  name="phone_number"
                  autoComplete="tel"
                  inputMode="tel"
                  pattern="[0-9()\-+\s]{7,20}"
                  maxLength={25}
                  value={form.phone_number}
                  onChange={onChange("phone_number")}
                  className="w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-2.5 text-gray-100 outline-none ring-indigo-500/30 focus:border-indigo-500 focus:ring-2"
                  placeholder="(555) 555-1234"
                  required
                  aria-invalid={!!errors.phone_number}
                  aria-describedby={errors.phone_number ? "err-phone" : undefined}
                />
                {errors.phone_number && (
                  <p id="err-phone" className="mt-1 text-sm text-red-400">
                    {errors.phone_number}
                  </p>
                )}
              </div>

              <div className="col-span-1">
                <label className="mb-1 block text-sm font-medium text-gray-200">Email Address</label>
                <input
                  type="email"
                  name="email_address"
                  autoComplete="email"
                  value={form.email_address}
                  onChange={onChange("email_address")}
                  className="w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-2.5 text-gray-100 outline-none ring-indigo-500/30 focus:border-indigo-500 focus:ring-2"
                  placeholder="jane@example.com"
                  required
                  aria-invalid={!!errors.email_address}
                  aria-describedby={errors.email_address ? "err-email" : undefined}
                />
                {errors.email_address && (
                  <p id="err-email" className="mt-1 text-sm text-red-400">
                    {errors.email_address}
                  </p>
                )}
              </div>

              <div className="col-span-1">
                <label className="mb-1 block text-sm font-medium text-gray-200">Major</label>
                <input
                  type="text"
                  name="major"
                  value={form.major}
                  onChange={onChange("major")}
                  className="w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-2.5 text-gray-100 outline-none ring-indigo-500/30 focus:border-indigo-500 focus:ring-2"
                  placeholder="Computer Science"
                  required
                  aria-invalid={!!errors.major}
                  aria-describedby={errors.major ? "err-major" : undefined}
                />
                {errors.major && (
                  <p id="err-major" className="mt-1 text-sm text-red-400">
                    {errors.major}
                  </p>
                )}
              </div>
            </div>

            {/* School dropdown */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-200">School</label>
              <select
                name="school"
                value={form.school}
                onChange={onSelectChange("school")}
                className="w-full rounded-lg border border-gray-700 bg-gray-900 px-4 py-2.5 text-gray-100 outline-none ring-indigo-500/30 focus:border-indigo-500 focus:ring-2"
                required
                aria-invalid={!!errors.school}
                aria-describedby={errors.school ? "err-school" : undefined}
              >
                <option value="" disabled>
                  — Select your school —
                </option>
                {SCHOOL_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {errors.school && (
                <p id="err-school" className="mt-1 text-sm text-red-400">
                  {errors.school}
                </p>
              )}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex w-full items-center justify-center rounded-xl bg-indigo-600 px-6 py-3 text-base font-medium text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {isLoading ? "Submitting..." : "Submit"}
              </button>
            </div>

            <p className="text-xs leading-relaxed text-gray-400">
              By submitting this form you agree to be contacted about Sigma Delta Alpha interest.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
};

export default InterestForm;
